import { ButtonStyled } from "components/buttons/buttons.styled";
import { Dropdown } from "components/dropdown/Dropdown";
import { Color } from "config/colors";
import { Filter, FilterType, GroupType } from "model/Calendar";
import { Entity } from "model/CompanyStructure";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Api } from "services/api/api.service";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useOrganizationalStructureStore } from "web-apps/company/stores/organizationalStructureStore/organizationalStructureStore";
import { LabelStyled } from "../clockStamp/clockStamp.styled";
import {
  DropDownContainer,
  FilterCalendarContainer,
  FilterColumn,
} from "./weeklyCalendar.styled";

type Props = {
  setFilter: React.Dispatch<React.SetStateAction<Filter[]>>;
};

export const FilterCalendar: React.FC<Props> = ({ setFilter }) => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const [orgState] = useOrganizationalStructureStore();
  const [entities, setEntities] = useState<Entity[]>([]);
  const [selectedEntityId, setSelectedEntityId] = useState<
    number | undefined
  >();
  const [selectedUnitId, setSelectedUnitId] = useState<number | undefined>();
  const [selectedWorkGroupId, setSelectedWorkGroupId] = useState<
    number | undefined
  >();

  const [selectedGroupBy, setSelectedGroupBy] = useState<GroupType>(
    GroupType.NONE
  );
  const [filterType, setFilterType] = useState<FilterType>(FilterType.NONE);

  const [localFilter, setLocalFilter] = useState<Filter[]>([
    {
      filterId: -1,
      groupType: GroupType.NONE,
      filterType: FilterType.NONE,
    },
  ]);

  const fetchEntities = () => {
    if (!companyState.company) return;

    Api()
      .company.organizationalStructure.fetchCompanyStructure(
        companyState.company.id
      )
      .then((res) => {
        setEntities(res.data.entities ?? []);
      })
      .catch(() => {
        // eslint-disable-next-line no-console
        console.log("Error fetching entities");
      });
  };

  useEffect(() => {
    if (
      (filterType === FilterType.COMPANYENTITY ||
        filterType === FilterType.COMPANYUNIT) &&
      entities.length === 0
    ) {
      fetchEntities();
    }
  }, [filterType, companyState.company]);

  const entityOptions = useMemo(
    () =>
      (entities ?? []).map((entity) => ({
        value: entity.id,
        label: entity.name,
      })),
    [entities]
  );

  const unitOptions = useMemo(() => {
    const seen = new Set<number>();
    const out: { value: number; label: string }[] = [];
    for (const e of entities ?? []) {
      for (const u of e.units ?? []) {
        if (!seen.has(u.id)) {
          seen.add(u.id);
          out.push({ value: u.id, label: u.name });
        }
      }
    }
    return out;
  }, [entities]);
  const workgroupOptions = useMemo(() => {
    const all = (orgState.structure?.entities ?? [])
      .flatMap((e) => e.units ?? [])
      .flatMap((u) => u.workGroups ?? []);
    return all.map((wg) => ({ value: wg.id, label: wg.name }));
  }, [orgState.structure]);

  useEffect(() => {
    setSelectedEntityId(undefined);
    setSelectedUnitId(undefined);
    setSelectedWorkGroupId(undefined);

    setLocalFilter([
      {
        filterId: -1,
        filterType,
        groupType: selectedGroupBy ?? GroupType.NONE,
      },
    ]);
  }, [filterType, selectedGroupBy]);

  return (
    <>
      <FilterCalendarContainer>
        <FilterColumn>
          <LabelStyled style={{ marginTop: 10, marginBottom: 4 }}>
            {t("Calendar.GroupBy")}
          </LabelStyled>
          <DropDownContainer>
            <Dropdown
              isCalendar
              textColor={Color.MidnightBlue}
              smallerFont
              placeholder={t("Calendar.GroupBy")}
              activeValue={selectedGroupBy}
              items={[
                { value: GroupType.UNIT, label: t("WorkGroup.Unit") },
                { value: GroupType.WORKGROUP, label: t("WorkGroup.WorkGroup") },
                { value: GroupType.NONE, label: t("Calendar.None") },
              ]}
              maxHeight={200}
              onChange={(e: any) => {
                const nextGroupBy: GroupType = e.value;
                setSelectedGroupBy(nextGroupBy);
                setFilter([
                  {
                    filterId: -1,
                    filterType: FilterType.NONE,
                    groupType: nextGroupBy,
                  },
                ]);
              }}
            />
          </DropDownContainer>
        </FilterColumn>

        <FilterColumn>
          <LabelStyled style={{ marginTop: 10, marginBottom: 4 }}>
            {t("Calendar.Filter")}
          </LabelStyled>
          <DropDownContainer>
            <Dropdown
              isCalendar
              textColor={Color.MidnightBlue}
              smallerFont
              placeholder={t("Calendar.Filter")}
              activeValue={filterType}
              items={[
                { value: FilterType.COMPANYUNIT, label: t("WorkGroup.Unit") },
                {
                  value: FilterType.COMPANYWORKGROUP,
                  label: t("WorkGroup.WorkGroup"),
                },
                { value: FilterType.NONE, label: t("Calendar.None") },
              ]}
              maxHeight={200}
              onChange={(e: any) => {
                const nextType: FilterType = e.value;
                setFilterType(nextType);
              }}
            />
          </DropDownContainer>
        </FilterColumn>

        {filterType === FilterType.COMPANYENTITY && (
          <FilterColumn>
            <LabelStyled style={{ marginTop: 10, marginBottom: 4 }}>
              {t("Calendar.Entity")}
            </LabelStyled>
            <DropDownContainer>
              <Dropdown
                isCalendar
                disabled={entityOptions.length <= 1}
                textColor={Color.MidnightBlue}
                smallerFont
                placeholder={t("Calendar.ChooseEntity")}
                activeValue={selectedEntityId}
                items={entityOptions}
                maxHeight={200}
                onChange={(e: any) => {
                  setSelectedEntityId(e.value as number);
                  setLocalFilter([
                    {
                      filterId: e.value as number,
                      filterType: FilterType.COMPANYENTITY,
                      groupType: selectedGroupBy ?? GroupType.NONE,
                    },
                  ]);
                }}
              />
            </DropDownContainer>
          </FilterColumn>
        )}

        {filterType === FilterType.COMPANYUNIT && (
          <FilterColumn>
            <LabelStyled style={{ marginTop: 10, marginBottom: 4 }}>
              {t("WorkGroup.Unit")}
            </LabelStyled>
            <DropDownContainer>
              <Dropdown
                isCalendar
                disabled={unitOptions.length <= 1}
                textColor={Color.MidnightBlue}
                smallerFont
                placeholder={t("Calendar.ChooseUnit")}
                activeValue={selectedUnitId}
                items={unitOptions}
                maxHeight={200}
                onChange={(e: any) => {
                  setSelectedUnitId(e.value as number);
                  setLocalFilter([
                    {
                      filterId: e.value as number,
                      filterType: FilterType.COMPANYUNIT,
                      groupType: selectedGroupBy ?? GroupType.NONE,
                    },
                  ]);
                }}
              />
            </DropDownContainer>
          </FilterColumn>
        )}

        {filterType === FilterType.COMPANYWORKGROUP && (
          <FilterColumn>
            <LabelStyled style={{ marginTop: 10, marginBottom: 4 }}>
              {t("WorkGroup.WorkGroup")}
            </LabelStyled>
            <DropDownContainer>
              <Dropdown
                isCalendar
                disabled={workgroupOptions.length === 0}
                textColor={Color.MidnightBlue}
                smallerFont
                placeholder={t("Calendar.ChooseWorkgroup")}
                activeValue={selectedWorkGroupId}
                items={workgroupOptions}
                maxHeight={200}
                onChange={(e: any) => {
                  setSelectedWorkGroupId(e.value as number);
                  setLocalFilter([
                    {
                      filterId: e.value as number,
                      filterType: FilterType.COMPANYWORKGROUP,
                      groupType: selectedGroupBy ?? GroupType.NONE,
                    },
                  ]);
                }}
              />
            </DropDownContainer>
          </FilterColumn>
        )}

        <FilterColumn>
          <ButtonStyled
            onClick={() => setFilter(localFilter)}
            backgroundColor={Color.ModernGreen}
            color={Color.White}
          >
            {t("CompanyTimeReport.Filter")}
          </ButtonStyled>
        </FilterColumn>
      </FilterCalendarContainer>
    </>
  );
};
